import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaUserCircle,
  FaClock,
  FaTimes,
  FaExternalLinkAlt,
  FaLink,
  FaArrowRight,
} from "react-icons/fa";
import { Bell } from "lucide-react";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import HeaderNavTrigger from "../Header/HeaderNavTrigger";
import HeaderPanelModal, {
  PanelEmptyState,
  PanelListSkeleton,
  panelBadgeTw,
  formatRelativeTime,
  MessagingSplitGrid,
  MessagingListColumn,
  MessagingListToolbar,
  MessagingDetailColumn,
  messagingListCardClass,
} from "../Header/HeaderPanelModal";
import { resolveNotificationAction } from "../../utilis/notificationRouteHelpers";

/** Laravel may send 0/1, "0"/"1", or booleans — treat only explicit read as read */
const isNotificationRead = (n) => {
  if (n == null) return false;
  const r = n.is_read;
  if (r === true) return true;
  const num = Number(r);
  return num === 1;
};

const sameNotificationId = (a, b) => String(a) === String(b);

const Notification = (props) => {
  const ref = useRef();
  const listFetchGen = useRef(0);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const hasAutoOpened = useRef(false);
  const POPUP_FLAG = "notificationPopupShown";

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (open && ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", checkIfClickedOutside);
    return () => {
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, [open]);

  // Auto open once when there are unread notifications (e.g., right after login)
  useEffect(() => {
    const hasShownPopup = localStorage.getItem(POPUP_FLAG) === "true";
    if (!hasAutoOpened.current && !hasShownPopup && props.count > 0 && !open) {
      setOpen(true);
      fetchNotifications({ showLoader: true });
      hasAutoOpened.current = true;
      localStorage.setItem(POPUP_FLAG, "true");
    }
  }, [props.count, open]);

  // Keep tabs in sync; if any tab sets the flag, others won't auto-open
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === POPUP_FLAG && e.newValue === "true") {
        hasAutoOpened.current = true;
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const readNotification = async (notification) => {
    // Drop stale GET /notifications responses so they cannot overwrite mark-as-read
    listFetchGen.current += 1;

    setNotifications((prev) =>
      prev.map((n) =>
        sameNotificationId(n.id, notification.id) ? { ...n, is_read: 1 } : n,
      ),
    );
    setSelectedNotification((prev) =>
      prev && sameNotificationId(prev.id, notification.id)
        ? { ...prev, is_read: 1 }
        : prev,
    );
    try {
      await apiUrl.post(`readNotification/${notification.id}`);
      if (props.onRead) {
        props.onRead();
      }
    } catch (error) {
      console.log(error);
      setNotifications((prev) =>
        prev.map((n) =>
          sameNotificationId(n.id, notification.id) ? { ...n, is_read: 0 } : n,
        ),
      );
      setSelectedNotification((prev) =>
        prev && sameNotificationId(prev.id, notification.id)
          ? { ...prev, is_read: 0 }
          : prev,
      );
    }
  };

  const fetchNotifications = async ({ showLoader = true } = {}) => {
    const gen = ++listFetchGen.current;
    try {
      if (showLoader) setLoading(true);
      const response = await apiUrl.get("/notifications");
      if (gen !== listFetchGen.current) {
        return [];
      }
      if (response?.status) {
        const list = response?.data?.data?.notifications || [];
        setNotifications(list);
        if (list.length > 0) {
          setSelectedNotification((prev) => {
            if (prev && list.some((n) => sameNotificationId(n.id, prev.id))) {
              return list.find((n) => sameNotificationId(n.id, prev.id)) ?? prev;
            }
            const firstUnread = list.find((n) => !isNotificationRead(n));
            return firstUnread ?? list[0];
          });
        } else {
          setSelectedNotification(null);
        }
        return list;
      }
    } catch (error) {
      console.log("Error fetching notifications:", error);
      if (gen === listFetchGen.current) {
        setNotifications([]);
      }
      return [];
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
    return [];
  };

  const handleIconClick = async () => {
    if (!open) {
      setOpen(true);
      fetchNotifications({ showLoader: true });
    } else {
      setOpen(false);
    }
  };

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    if (!isNotificationRead(notification)) {
      readNotification(notification);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Just now";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const extractLink = (notification) => {
    if (!notification) return null;
    if (notification.link) return notification.link;
    const payload = notification.payload;
    if (payload && typeof payload === "object" && payload.link)
      return payload.link;
    if (typeof payload === "string") {
      const urlRegex = /(https?:\/\/[^\s]+)/i;
      const match = payload.match(urlRegex);
      if (match && match[0]) return match[0];
    }
    return null;
  };

  const NotificationTypeIcon = ({ type, className = "" }) => {
    if (type === 6) {
      return <FaUserCircle className={`text-blue-600 ${className}`} />;
    }
    return <Bell className={`text-amber-500 ${className}`} size={20} strokeWidth={2} />;
  };

  const getNotificationBadgeTw = (notification) => {
    switch (notification.type) {
      case 6:
        return panelBadgeTw.primary;
      case 3:
        return panelBadgeTw.success;
      case 2:
        return panelBadgeTw.danger;
      case 5:
        return panelBadgeTw.info;
      default:
        return panelBadgeTw.secondary;
    }
  };

  const getNotificationTypeLabel = (type) => {
    if (type === 6) return "Client";
    if (type === 3) return "Education App";
    if (type === 2) return "Migration App";
    if (type === 5) return "Finance";
    return "General";
  };

  const selectedAction = selectedNotification
    ? resolveNotificationAction(selectedNotification)
    : null;

  return (
    <>
      <HeaderNavTrigger
        label="Notification"
        onClick={handleIconClick}
        badgeCount={props.count}
      >
        {props.icon}
      </HeaderNavTrigger>

      <HeaderPanelModal
        open={open}
        onClose={() => setOpen(false)}
        title="Notifications"
        subtitle={
          props.count > 0 ? `${props.count} unread` : "Stay up to date"
        }
        headerIcon={<Bell size={18} strokeWidth={2} className="text-white" />}
        panelRef={ref}
        ariaLabelledBy="header-notifications-title"
        footer={
          notifications?.length > 0 ? (
            <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-2.5">
              <span className="text-[11px] text-slate-500">
                Showing {notifications.length} recent
              </span>
              <Link
                to="/notifications/view-all"
                className="inline-flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1 text-xs font-semibold text-[#0b2166] shadow-sm ring-1 ring-slate-200 hover:bg-slate-50"
                onClick={() => setOpen(false)}
              >
                View all
                <FaArrowRight className="text-[10px]" />
              </Link>
            </div>
          ) : null
        }
      >
        <MessagingSplitGrid>
          <MessagingListColumn
            toolbar={
              <MessagingListToolbar
                title="Recent"
                trailing={
                  props.count > 0 ? (
                    <span
                      className="rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold text-white"
                    >
                      {props.count} new
                    </span>
                  ) : (
                    <span className="text-[11px] text-slate-400">All read</span>
                  )
                }
              />
            }
          >
              {loading ? (
                <PanelListSkeleton rows={5} />
              ) : notifications.length === 0 ? (
                <PanelEmptyState
                  icon={<Bell size={28} strokeWidth={1.75} />}
                  title="No notifications"
                  description="You're all caught up with your notifications"
                />
              ) : (
                <ul className="m-0 list-none space-y-1.5 p-0">
                  {notifications.map((notification) => {
                    const isActive =
                      selectedNotification?.id === notification.id;
                    const isUnread = !isNotificationRead(notification);
                    const typeLabel = getNotificationTypeLabel(
                      notification.type,
                    );
                    return (
                      <li key={notification.id}>
                        <button
                          type="button"
                          onClick={() =>
                            handleNotificationClick(notification)
                          }
                          className={messagingListCardClass(
                            isActive,
                            isUnread,
                          )}
                        >
                          <div className="flex gap-3">
                            <div
                              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                                isUnread ? "bg-blue-100" : "bg-slate-200/70"
                              }`}
                            >
                              <NotificationTypeIcon
                                type={notification.type}
                                className="text-lg"
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start gap-2">
                                <p
                                  className={`mb-0 line-clamp-2 flex-1 text-[13px] leading-snug ${
                                    isUnread
                                      ? "font-semibold text-slate-900"
                                      : "font-medium text-slate-600"
                                  }`}
                                >
                                  {notification.title}
                                </p>
                                {isUnread ? (
                                  <span
                                    className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-600"
                                    aria-hidden
                                  />
                                ) : null}
                              </div>
                              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                <span
                                  className={getNotificationBadgeTw(
                                    notification,
                                  )}
                                >
                                  {typeLabel}
                                </span>
                                <span className="text-[10px] text-slate-400">
                                  · {formatRelativeTime(notification.created_at)}
                                </span>
                              </div>
                              {notification?.client?.name ? (
                                <p
                                  className="mb-0 mt-1.5 truncate text-[11px] font-medium text-blue-700"
                                >
                                  <FaUserCircle className="mr-1 inline text-[10px]" />
                                  {notification.client.name}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
          </MessagingListColumn>

          <MessagingDetailColumn>
            {selectedNotification ? (
              <>
                <div
                  className="shrink-0 border-b border-slate-100 bg-gradient-to-b from-slate-50 to-white px-5 py-4"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-50"
                    >
                      <NotificationTypeIcon
                        type={selectedNotification.type}
                        className="text-xl"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-start gap-2">
                        <h3
                          className="mb-0 flex-1 text-base font-semibold leading-snug text-slate-900"
                        >
                          {selectedNotification.title}
                        </h3>
                        {!isNotificationRead(selectedNotification) ? (
                          <span
                            className="shrink-0 rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
                          >
                            Unread
                          </span>
                        ) : null}
                      </div>
                      <div
                        className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500"
                      >
                        <span className="inline-flex items-center gap-1">
                          <FaClock className="opacity-70" />
                          {formatDate(selectedNotification.created_at)}
                        </span>
                        <span
                          className={getNotificationBadgeTw(
                            selectedNotification,
                          )}
                        >
                          {getNotificationTypeLabel(selectedNotification.type)}
                        </span>
                        {selectedNotification?.client?.name ? (
                          <span className="font-medium text-blue-700">
                            {selectedNotification.client.name}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="cms-panel-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
                  <p
                    className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400"
                  >
                    Message
                  </p>
                  <div
                    className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3.5 text-sm leading-relaxed text-slate-700 [&_a]:text-blue-700 [&_a]:underline"
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: selectedNotification.message,
                      }}
                    />
                  </div>

                  {selectedNotification?.notes ? (
                    <div className="mt-4">
                      <p
                        className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400"
                      >
                        Notes
                      </p>
                      <div
                        className="rounded-xl border border-amber-200/80 bg-amber-50/50 px-4 py-3 text-sm text-slate-700"
                      >
                        {selectedNotification.notes}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div
                  className="flex shrink-0 flex-wrap items-center gap-2 border-t border-slate-200 bg-slate-50/80 px-5 py-3"
                >
                  {selectedAction?.to ? (
                    <Link
                      to={selectedAction.to}
                      className="inline-flex items-center gap-2 rounded-lg bg-[#0b2166] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#1a3a8a]"
                      onClick={() => setOpen(false)}
                    >
                      <FaLink className="text-[11px]" />
                      {selectedAction.label}
                    </Link>
                  ) : null}
                  {extractLink(selectedNotification) ? (
                    <a
                      href={extractLink(selectedNotification)}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-white"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaExternalLinkAlt className="text-[11px]" />
                      External link
                    </a>
                  ) : null}
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-slate-500 hover:bg-white hover:text-slate-700"
                    onClick={() => setSelectedNotification(null)}
                  >
                    <FaTimes className="text-[10px]" />
                    Deselect
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center p-6">
                <PanelEmptyState
                  icon={<Bell size={22} strokeWidth={1.75} />}
                  title="Select a notification"
                  description="Pick an item from the list to read the full message"
                />
              </div>
            )}
          </MessagingDetailColumn>
        </MessagingSplitGrid>
      </HeaderPanelModal>
    </>
  );
};

export default Notification;
